import React, { useState, useEffect } from 'react';
import { Button, Badge, Input, Loader, Select } from './shared';

const AutomatedTesting = ({ service, isOnline }) => {
  const [projects, setProjects] = useState([]);
  const [executions, setExecutions] = useState([]);
  const [stats, setStats] = useState(null);
  const [templates, setTemplates] = useState({});
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showCreateProject, setShowCreateProject] = useState(false);
  const [newProject, setNewProject] = useState({
    name: '',
    description: '',
    project_type: 'django_react',
    frontend_url: '',
    backend_url: '',
    admin_url: '',
    template: '',
    client_info: {
      client_name: '',
      contact_email: '',
      sla_uptime: 99.9,
      sla_response_time: 2000
    },
    schedule: {
      frequency: 'daily',
      time_of_day: '02:00'
    },
    test_types_enabled: ['health_check', 'api_testing']
  });

  // eslint-disable-next-line no-unused-vars
  const [selectedProject, setSelectedProject] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [executionDetails, setExecutionDetails] = useState(null);

  // Fetch data from testing service
  const fetchData = async () => {
    if (!isOnline) return;
    
    try {
      setLoading(true);
      
      // Fetch projects, stats, templates, and recent executions in parallel
      const [projectsRes, statsRes, templatesRes, executionsRes, alertsRes] = await Promise.all([
        fetch(`${service.url}/projects`),
        fetch(`${service.url}/stats`),
        fetch(`${service.url}/project-templates`),
        fetch(`${service.url}/executions?limit=10`),
        fetch(`${service.url}/alerts?limit=5`)
      ]);

      if (projectsRes.ok) {
        const projectsData = await projectsRes.json();
        setProjects(projectsData);
      }

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }

      if (templatesRes.ok) {
        const templatesData = await templatesRes.json();
        setTemplates(templatesData.templates || {});
      }

      if (executionsRes.ok) {
        const executionsData = await executionsRes.json();
        setExecutions(executionsData);
      }

      if (alertsRes.ok) {
        const alertsData = await alertsRes.json();
        setAlerts(alertsData);
      }

    } catch (error) {
      console.error('Failed to fetch testing data:', error);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, [isOnline]);

  const handleCreateProject = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${service.url}/projects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newProject)
      });

      if (response.ok) {
        await fetchData();
        setShowCreateProject(false);
        setNewProject({
          name: '',
          description: '',
          project_type: 'django_react',
          frontend_url: '',
          backend_url: '',
          admin_url: '',
          template: '',
          client_info: {
            client_name: '',
            contact_email: '',
            sla_uptime: 99.9,
            sla_response_time: 2000
          },
          schedule: {
            frequency: 'daily',
            time_of_day: '02:00'
          },
          test_types_enabled: ['health_check', 'api_testing']
        });
      } else {
        alert('Failed to create project');
      }
    } catch (error) {
      console.error('Error creating project:', error);
      alert('Error creating project');
    } finally {
      setLoading(false);
    }
  };

  const triggerTests = async (projectId) => {
    setLoading(true);
    try {
      const response = await fetch(`${service.url}/projects/${projectId}/test`, {
        method: 'POST'
      });

      if (response.ok) {
        const result = await response.json();
        alert(`Tests triggered successfully! Execution ID: ${result.execution_id}`);
        await fetchData();
      } else {
        alert('Failed to trigger tests');
      }
    } catch (error) {
      console.error('Error triggering tests:', error);
      alert('Error triggering tests');
    } finally {
      setLoading(false);
    }
  };

  const applyTemplate = (templateName) => {
    if (templates[templateName]) {
      const template = templates[templateName];
      setNewProject(prev => ({
        ...prev,
        name: `${prev.client_info.client_name || 'Client'} ${template.name}`,
        description: template.description,
        project_type: template.project_type,
        template: templateName,
        test_types_enabled: template.default_tests || ['health_check', 'api_testing']
      }));
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  if (!isOnline) {
    return (
      <div className="p-6 text-center">
        <div className="text-red-600 mb-4">
          <svg className="w-12 h-12 mx-auto mb-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Testing Service Offline</h3>
        <p className="text-gray-600">The Automated Testing Service is not available. Please check the service status.</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {['dashboard', 'projects', 'executions', 'alerts'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-2 px-1 border-b-2 font-medium text-sm capitalize ${
                activeTab === tab
                  ? 'border-[#FFC65D] text-[#FFC65D]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab}
            </button>
          ))}
        </nav>
      </div>

      {loading && (
        <div className="flex justify-center items-center py-8">
          <Loader size="md" />
        </div>
      )}

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && (
        <div>
          {/* Service Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
              <div className="flex items-center">
                <div className="text-2xl font-bold text-gray-600">{stats?.total_projects || 0}</div>
                <div className="ml-3 text-sm font-medium text-gray-900">Total Projects</div>
              </div>
            </div>
            <div className="bg-green-50 p-6 rounded-lg border border-green-200">
              <div className="flex items-center">
                <div className="text-2xl font-bold text-green-600">{stats?.active_projects || 0}</div>
                <div className="ml-3 text-sm font-medium text-green-900">Active Projects</div>
              </div>
            </div>
            <div className="bg-purple-50 p-6 rounded-lg border border-purple-200">
              <div className="flex items-center">
                <div className="text-2xl font-bold text-purple-600">{stats?.total_executions || 0}</div>
                <div className="ml-3 text-sm font-medium text-purple-900">Total Tests</div>
              </div>
            </div>
            <div className="bg-orange-50 p-6 rounded-lg border border-orange-200">
              <div className="flex items-center">
                <div className="text-2xl font-bold text-orange-600">{stats?.average_success_rate?.toFixed(1) || 0}%</div>
                <div className="ml-3 text-sm font-medium text-orange-900">Success Rate</div>
              </div>
            </div>
          </div>

          {/* Recent Executions */}
          <div className="bg-white border border-gray-200 rounded-lg mb-6">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Recent Test Executions</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase">Project</th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase">Started</th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase">Duration</th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase">Success Rate</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {executions.slice(0, 5).map((execution) => (
                    <tr key={execution.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {execution.project_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge variant={
                          execution.status === 'completed' ? 'success' :
                          execution.status === 'running' ? 'info' :
                          execution.status === 'failed' ? 'danger' :
                          'warning'
                        }>
                          {execution.status}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {execution.start_time ? formatDate(execution.start_time) : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {execution.duration_seconds ? `${execution.duration_seconds.toFixed(1)}s` : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {execution.success_rate?.toFixed(1) || 0}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Recent Alerts */}
          {alerts.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Recent Alerts</h3>
              </div>
              <div className="divide-y divide-gray-200">
                {alerts.slice(0, 3).map((alert) => (
                  <div key={alert.id} className="px-6 py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <Badge variant={
                            alert.severity === 'critical' ? 'danger' :
                            alert.severity === 'high' ? 'warning' :
                            alert.severity === 'medium' ? 'warning' :
                            'info'
                          }>
                            {alert.severity}
                          </Badge>
                          <h4 className="text-sm font-medium text-gray-900">{alert.title}</h4>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{alert.message}</p>
                      </div>
                      <div className="text-sm text-gray-500">
                        {formatDate(alert.triggered_at)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Projects Tab */}
      {activeTab === 'projects' && (
        <div>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Testing Projects</h2>
            <Button
              onClick={() => setShowCreateProject(true)}
              variant="primary"
            >
              Create Project
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {projects.map((project) => (
              <div key={project.id} className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">{project.name}</h3>
                    <p className="text-sm text-gray-600">{project.description}</p>
                  </div>
                  <Badge variant={project.enabled ? 'success' : 'info'}>
                    {project.enabled ? 'Active' : 'Disabled'}
                  </Badge>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Client:</span>
                    <span className="font-medium">{project.client_info.client_name}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Type:</span>
                    <span className="font-medium">{project.project_type}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Schedule:</span>
                    <span className="font-medium">{project.schedule.frequency}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">SLA:</span>
                    <span className="font-medium">{project.client_info.sla_uptime}% uptime</span>
                  </div>
                </div>

                <div className="flex space-x-2">
                  <Button
                    onClick={() => triggerTests(project.id)}
                    disabled={loading}
                    variant="primary"
                    style={{ flex: 1 }}
                  >
                    Run Tests
                  </Button>
                  <Button
                    onClick={() => setSelectedProject(project)}
                    variant="secondary"
                    style={{ flex: 1 }}
                  >
                    View Details
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {projects.length === 0 && (
            <div className="text-center py-12">
              <h3 className="text-lg font-medium text-gray-900 mb-2">No projects configured</h3>
              <p className="text-gray-600 mb-4">Create your first testing project to get started</p>
              <Button
                onClick={() => setShowCreateProject(true)}
                variant="primary"
              >
                Create Project
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Executions Tab */}
      {activeTab === 'executions' && (
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Test Executions</h2>
          
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase">Project</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase">Started</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase">Duration</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase">Tests</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase">Success Rate</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {executions.map((execution) => (
                  <tr key={execution.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {execution.project_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge variant={
                        execution.status === 'completed' ? 'success' :
                        execution.status === 'running' ? 'info' :
                        execution.status === 'failed' ? 'danger' :
                        'warning'
                      }>
                        {execution.status}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {execution.start_time ? formatDate(execution.start_time) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {execution.duration_seconds ? `${execution.duration_seconds.toFixed(1)}s` : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {execution.passed_tests || 0} / {execution.total_tests || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {execution.success_rate?.toFixed(1) || 0}%
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => setExecutionDetails(execution)}
                        className="text-[#FFC65D] hover:text-[#FFC65D] hover:opacity-80"
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Alerts Tab */}
      {activeTab === 'alerts' && (
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-6">System Alerts</h2>
          
          <div className="space-y-4">
            {alerts.map((alert) => (
              <div key={alert.id} className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <Badge variant={
                      alert.severity === 'critical' ? 'danger' :
                      alert.severity === 'high' ? 'warning' :
                      alert.severity === 'medium' ? 'warning' :
                      'info'
                    }>
                      {alert.severity}
                    </Badge>
                    <h3 className="text-lg font-medium text-gray-900">{alert.title}</h3>
                  </div>
                  <div className="text-sm text-gray-500">
                    {formatDate(alert.triggered_at)}
                  </div>
                </div>
                <p className="text-gray-600 mb-2">{alert.message}</p>
                {alert.test_name && (
                  <p className="text-sm text-gray-500">Related Test: {alert.test_name}</p>
                )}
              </div>
            ))}

            {alerts.length === 0 && (
              <div className="text-center py-12">
                <h3 className="text-lg font-medium text-gray-900 mb-2">No alerts</h3>
                <p className="text-gray-600">All systems are running normally</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create Project Modal */}
      {showCreateProject && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-screen overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Create Testing Project</h2>
            </div>
            
            <form onSubmit={handleCreateProject} className="p-6">
              {/* Template Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Project Template</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {Object.entries(templates).map(([key, template]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => applyTemplate(key)}
                      className="text-left p-3 border border-gray-300 rounded-md hover:border-[#FFC65D] hover:bg-[#FFC65D] hover:bg-opacity-10"
                    >
                      <div className="font-medium text-sm">{template.name}</div>
                      <div className="text-xs text-gray-500">{template.description}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <Input
                    type="text"
                    label="Project Name"
                    value={newProject.name}
                    onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                    required
                    fullWidth
                  />
                </div>
                <div>
                  <Input
                    type="text"
                    label="Client Name"
                    value={newProject.client_info.client_name}
                    onChange={(e) => setNewProject({
                      ...newProject,
                      client_info: { ...newProject.client_info, client_name: e.target.value }
                    })}
                    required
                    fullWidth
                  />
                </div>
              </div>

              <div className="mb-4">
                <Input
                  type="textarea"
                  label="Description"
                  value={newProject.description}
                  onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                  rows={2}
                  fullWidth
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <Input
                    type="url"
                    label="Frontend URL"
                    value={newProject.frontend_url}
                    onChange={(e) => setNewProject({ ...newProject, frontend_url: e.target.value })}
                    placeholder="https://example.com"
                    fullWidth
                  />
                </div>
                <div>
                  <Input
                    type="url"
                    label="Backend URL"
                    value={newProject.backend_url}
                    onChange={(e) => setNewProject({ ...newProject, backend_url: e.target.value })}
                    placeholder="https://api.example.com"
                    fullWidth
                  />
                </div>
                <div>
                  <Input
                    type="url"
                    label="Admin URL"
                    value={newProject.admin_url}
                    onChange={(e) => setNewProject({ ...newProject, admin_url: e.target.value })}
                    placeholder="https://admin.example.com"
                    fullWidth
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <Input
                    type="email"
                    label="Contact Email"
                    value={newProject.client_info.contact_email}
                    onChange={(e) => setNewProject({
                      ...newProject,
                      client_info: { ...newProject.client_info, contact_email: e.target.value }
                    })}
                    required
                    fullWidth
                  />
                </div>
                <div>
                  <Select
                    label="Test Frequency"
                    value={newProject.schedule.frequency}
                    onChange={(e) => setNewProject({
                      ...newProject,
                      schedule: { ...newProject.schedule, frequency: e.target.value }
                    })}
                    options={[
                      { value: 'hourly', label: 'Hourly' },
                      { value: 'daily', label: 'Daily' },
                      { value: 'weekly', label: 'Weekly' },
                      { value: 'monthly', label: 'Monthly' }
                    ]}
                    fullWidth
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <Button
                  type="button"
                  onClick={() => setShowCreateProject(false)}
                  variant="secondary"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  variant="primary"
                >
                  {loading ? 'Creating...' : 'Create Project'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AutomatedTesting;